import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Flex, Text, Heading, Badge, HStack, VStack,
  Center, Spinner, Icon, Button, RadioGroup, Radio, useToast,
  Select as ChakraSelect,
} from "@chakra-ui/react";
import { FiActivity, FiChevronLeft, FiChevronRight, FiCheckCircle } from "react-icons/fi";
import Select from "react-select";
import DateRangeFilter from "components/DateRangeFilter/DateRangeFilter";
import { useDateFilter } from "Hooks/useDateFilter";
import { getCurrentDate, getFirstDayOfMonth, parseDate } from "Utils/dateUtils";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "Configuration/apiConfig";
import { generatePresignedUrl } from "Utils/s3Utils";
import { CoolerImagePanel } from "./components/CoolerImagePanel";
import { StoreInfoPanel } from "./components/StoreInfoPanel";
import { ShelfSummaryTable } from "./components/ShelfSummaryTable";

// ─── Constants
const BRAND = "#C41E3A";
const PAGE_SIZE = 1;

const buildDateBody = (from, to) => {
  const { year: fy, month: fm, day: fd } = parseDate(from);
  const { year: ty, month: tm, day: td } = parseDate(to);
  return {
    from: { date: fd, month: fm, year: fy },
    to:   { date: td, month: tm, year: ty },
  };
};

const storeSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "white",
    borderColor:     state.isFocused ? BRAND : "#E2E8F0",
    borderRadius:    "12px",
    minHeight:       "44px",
    boxShadow:       state.isFocused ? `0 0 0 1px ${BRAND}` : "0 1px 3px rgba(0,0,0,0.06)",
    "&:hover":       { borderColor: "#CBD5E0" },
  }),
  singleValue:        (base) => ({ ...base, color: "#2D3748", fontWeight: "600" }),
  placeholder:        (base) => ({ ...base, color: "#A0AEC0" }),
  indicatorSeparator: ()     => ({ display: "none" }),
  loadingMessage:     (base) => ({ ...base, color: "#A0AEC0", fontSize: "14px" }),
  noOptionsMessage:   (base) => ({ ...base, color: "#A0AEC0", fontSize: "14px" }),
  menu:  (base) => ({
    ...base, zIndex: 9999, borderRadius: "12px",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? BRAND : state.isFocused ? "#FFF5F5" : "white",
    color:           state.isSelected ? "white" : "#2D3748",
    fontWeight:      state.isSelected ? "600" : "400",
  }),
};


export default function ManualShelfCounts() {
  const { fromDate, toDate, setFromDate, setToDate } = useDateFilter("manualShelfCounts", {
    fromDate: getFirstDayOfMonth(),
    toDate:   getCurrentDate(),
  });

  const [checkedFilter, setCheckedFilter] = useState("not-checked");

  const [statusCounts, setStatusCounts] = useState({ notMarked: 0, marked: 0 });
  const [loadingStatusCounts, setLoadingStatusCounts] = useState(false);

  const [storeOptions,  setStoreOptions]  = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [storeError,    setStoreError]    = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const storeOptionsRef = useRef([]);
  useEffect(() => { storeOptionsRef.current = storeOptions; }, [storeOptions]);

  const [auditorOptions,  setAuditorOptions]  = useState([]);
  const [loadingAuditors, setLoadingAuditors] = useState(false);
  const [auditorError,    setAuditorError]    = useState(null);
  const [selectedAuditor, setSelectedAuditor] = useState(null);

  const [page,          setPage]          = useState(0);
  const [pageInfo,      setPageInfo]      = useState(null); 
  const [storeRecord,   setStoreRecord]   = useState(null); 
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError,   setReportError]   = useState(null);

  const [shelfIndex,    setShelfIndex]    = useState(0);

  const [imageIndex,    setImageIndex]    = useState(0);
  const [imageRotation, setImageRotation] = useState(0);
  const [coolerImages,  setCoolerImages]  = useState([]);

  const [validating,    setValidating]    = useState(false);
  const [isValidated,   setIsValidated]   = useState(false);
  const [savingRowKey,  setSavingRowKey]  = useState(null);
  const [savingPurity,  setSavingPurity]  = useState(false);
  const [savingPlanogram, setSavingPlanogram] = useState(false);
  const toast = useToast();

  const [availabilityProducts, setAvailabilityProducts] = useState([]);
  const [loadingProducts,      setLoadingProducts]       = useState(false);
  const [addingProductShelf,   setAddingProductShelf]    = useState(null);
  const [deletingRowKey,       setDeletingRowKey]        = useState(null);

  // ── Fetch store list
  const fetchStores = useCallback(async (from, to, status) => {
    setLoadingStores(true);
    setStoreError(null);
    setStoreOptions([]);
    try {
      const res = await apiClient.post(
        `${API_CONFIG.ENDPOINTS.MANUALSHELFCOUNTS.STORES}?status=${status}`,
        buildDateBody(from, to)
      );
      const opts = (res.data || []).map((s) => ({
        value: s.storeId,
        label: s.storeName,
      }));
      setStoreOptions(opts);
    } catch (err) {
      console.error("fetchStores failed:", err?.response?.status, err?.response?.data || err?.message);
      setStoreError("Failed to load stores. Please check your connection and try again.");
    } finally {
      setLoadingStores(false);
    }
  }, []);

  // ── Fetch auditor list 
  const fetchAuditors = useCallback(async (from, to) => {
    setLoadingAuditors(true);
    setAuditorError(null);
    setAuditorOptions([]);
    try {
      const res = await apiClient.post(
        API_CONFIG.ENDPOINTS.MANUALSHELFCOUNTS.AUDITORS,
        buildDateBody(from, to)
      );
      const opts = (res.data || []).map((a) => ({
        value: a.userId,
        label: `${a.auditorCode} - ${a.auditorName}`,
        userId: a.userId,
        auditorCode: a.auditorCode,
        auditorName: a.auditorName,
      }));
      setAuditorOptions(opts);
    } catch (err) {
      console.error("fetchAuditors failed:", err?.response?.status, err?.response?.data || err?.message);
      setAuditorError("Failed to load auditors. Please check your connection and try again.");
    } finally {
      setLoadingAuditors(false);
    }
  }, []);

  // ── Fetch master list of available products (for the shelf "Add Product" dropdown)
  const fetchAvailabilityProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const res = await apiClient.get(API_CONFIG.ENDPOINTS.MANUALSHELFCOUNTS.AVAILABILITY_PRODUCTS);
      setAvailabilityProducts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("fetchAvailabilityProducts failed:", err?.response?.status, err?.response?.data || err?.message);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailabilityProducts();
  }, [fetchAvailabilityProducts]);

  // ── Fetch total store counts for the Marked / Not Marked filters ──────────
  // When auditorUserId is set, scopes the counts to that auditor's stores.
  const fetchStatusCounts = useCallback(async (from, to, auditorUserId) => {
    setLoadingStatusCounts(true);
    try {
      const body = buildDateBody(from, to);
      const [notCheckedRes, checkedRes] = auditorUserId != null
        ? await Promise.all([
            apiClient.post(`${API_CONFIG.ENDPOINTS.MANUALSHELFCOUNTS.AI_VS_MANUAL_REPORT_BY_AUDITOR}?page=0&size=1&userId=${auditorUserId}&status=not-checked`, body),
            apiClient.post(`${API_CONFIG.ENDPOINTS.MANUALSHELFCOUNTS.AI_VS_MANUAL_REPORT_BY_AUDITOR}?page=0&size=1&userId=${auditorUserId}&status=checked`, body),
          ])
        : await Promise.all([
            apiClient.post(`${API_CONFIG.ENDPOINTS.MANUALSHELFCOUNTS.AI_VS_MANUAL_REPORT}?page=0&size=1`, body),
            apiClient.post(`${API_CONFIG.ENDPOINTS.MANUALSHELFCOUNTS.AI_VS_MANUAL_REPORT_CHECKED}?page=0&size=1`, body),
          ]);
      setStatusCounts({
        notMarked: notCheckedRes.data?.totalElements ?? 0,
        marked: checkedRes.data?.totalElements ?? 0,
      });
    } catch (err) {
      console.error("fetchStatusCounts failed:", err?.response?.status, err?.response?.data || err?.message);
      setStatusCounts({ notMarked: 0, marked: 0 });
    } finally {
      setLoadingStatusCounts(false);
    }
  }, []);

  // ── Fetch one page (one store) of the paginated ai-vs-manual report ───────
  // When auditorUserId is set, browses that auditor's records instead of the checked/not-checked list.
  const fetchReport = useCallback(async (pageNum, from, to, checked, auditorUserId) => {
    setLoadingReport(true);
    setReportError(null);
    try {
      const url = auditorUserId != null
        ? `${API_CONFIG.ENDPOINTS.MANUALSHELFCOUNTS.AI_VS_MANUAL_REPORT_BY_AUDITOR}?page=${pageNum}&size=${PAGE_SIZE}&userId=${auditorUserId}&status=${checked}`
        : `${
            checked === "checked"
              ? API_CONFIG.ENDPOINTS.MANUALSHELFCOUNTS.AI_VS_MANUAL_REPORT_CHECKED
              : API_CONFIG.ENDPOINTS.MANUALSHELFCOUNTS.AI_VS_MANUAL_REPORT
          }?page=${pageNum}&size=${PAGE_SIZE}`;
      const res = await apiClient.post(url, buildDateBody(from, to));
      const data = res.data || {};
      const record = (data.content || [])[0] || null;

      setStoreRecord(record);
      setPageInfo({
        first: data.first ?? pageNum === 0,
        last: data.last ?? true,
        totalElements: data.totalElements ?? 0,
        totalPages: data.totalPages ?? 0,
      });
      setPage(pageNum);
      setShelfIndex(0);
      setImageIndex(0);
      setImageRotation(0);
      setIsValidated(auditorUserId != null ? false : checked === "checked");

      if (record?.storeDetail) {
        const match = storeOptionsRef.current.find((o) => o.value === record.storeDetail.storeId);
        setSelectedStore(
          match || { value: record.storeDetail.storeId, label: record.storeDetail.storeName }
        );
      }

      if (record?.coolerImage) {
        const { month, year } = parseDate(to);
        const url = await generatePresignedUrl(record.coolerImage, { month, year });
        setCoolerImages(url ? [url] : []);
      } else {
        setCoolerImages([]);
      }
    } catch {
      setStoreRecord(null);
      setPageInfo(null);
      setCoolerImages([]);
      setReportError("Failed to load report data. Please check your connection and try again.");
    } finally {
      setLoadingReport(false);
    }
  }, []);

  const selectedAuditorRef = useRef(null);
  useEffect(() => { selectedAuditorRef.current = selectedAuditor; }, [selectedAuditor]);

 
  useEffect(() => {
    if (fromDate && toDate && fromDate <= toDate) {
      fetchStores(fromDate, toDate, checkedFilter);
      fetchAuditors(fromDate, toDate);
      fetchReport(0, fromDate, toDate, checkedFilter, selectedAuditorRef.current?.userId);
    }
  }, [fromDate, toDate, checkedFilter, fetchStores, fetchAuditors, fetchReport]);

  
  useEffect(() => {
    if (fromDate && toDate && fromDate <= toDate) {
      fetchStatusCounts(fromDate, toDate, selectedAuditor?.userId);
    }
  }, [fromDate, toDate, selectedAuditor, fetchStatusCounts]);

 
  const handleFromDateChange = (val) => setFromDate(val);
  const handleToDateChange   = (val) => setToDate(val);

  
  const handleCheckedFilterChange = (val) => {
    setCheckedFilter(val);
    setSelectedAuditor(null);
  };

 
  const handleStoreChange = (option) => {
    setSelectedStore(option);
    setSelectedAuditor(null);
    if (!option) {
      fetchReport(0, fromDate, toDate, checkedFilter);
      return;
    }
    const idx = storeOptions.findIndex((o) => o.value === option.value);
    fetchReport(idx >= 0 ? idx : 0, fromDate, toDate, checkedFilter);
  };

  const handleAuditorChange = (option) => {
    setSelectedAuditor(option);
    setSelectedStore(null);
    fetchReport(0, fromDate, toDate, checkedFilter, option?.userId);
  };

 
  const handlePrevStore = () => {
    if (page > 0) fetchReport(page - 1, fromDate, toDate, checkedFilter, selectedAuditor?.userId);
  };
  const handleNextStore = () => {
    if (pageInfo && !pageInfo.last) fetchReport(page + 1, fromDate, toDate, checkedFilter, selectedAuditor?.userId);
  };


  const totalShelves = storeRecord?.shelves?.length ?? 0;
  const handlePrevShelf = () => setShelfIndex((i) => Math.max(0, i - 1));
  const handleNextShelf = () => setShelfIndex((i) => Math.min(totalShelves - 1, i + 1));

  const storeDetail = storeRecord?.storeDetail ?? null;
  const currentShelf = storeRecord?.shelves?.[shelfIndex] ?? null;

 
  const handleValidateStore = async () => {
    const runId = storeRecord?.shelves
      ?.flatMap((s) => s.productCounts ?? [])
      .find((p) => p.runId != null)?.runId;
    const storeId = storeDetail?.storeId;
    const auditDate = storeDetail?.auditedDate;
    const userId = localStorage.getItem("userId") || sessionStorage.getItem("userId");

    if (!runId || !storeId || !auditDate) {
      toast({ title: "Missing store/run details, cannot validate.", status: "error", duration: 3000, isClosable: true, position: "top" });
      return;
    }

    setValidating(true);
    try {
      await apiClient.post(API_CONFIG.ENDPOINTS.MANUALSHELFCOUNTS.VALIDATE_STORE, {
        runId,
        storeId,
        auditType: "MACHK",
        auditDate,
        userId,
      });
      toast({ title: "Store validated successfully", status: "success", duration: 2000, isClosable: true, position: "top" });
      setIsValidated(true);
      fetchStatusCounts(fromDate, toDate, selectedAuditor?.userId);
    } catch {
      toast({ title: "Failed to validate store. Please try again.", status: "error", duration: 3000, isClosable: true, position: "top" });
    } finally {
      setValidating(false);
    }
  };


  
  // Each item in `productCounts` may carry its own `action` tag ("ADD" / "DELETE").
  // Untagged items default to null, as required by the update-shelf-report API.
  const handleUpdateShelf = async (shelfNumber, { productCounts, shelfPurity, planogram } = {}) => {
    const shelf = storeRecord?.shelves?.find((s) => s.shelfNumber === shelfNumber);
    if (!shelf) return;

    const requestProductCounts = productCounts ?? shelf.productCounts;
    const nextShelfPurity = shelfPurity ?? shelf.shelfPurity ?? "N";
    const nextPlanogram = planogram ?? shelf.planogram ?? "N";

    const payload = {
      shelves: [
        {
          shelfNumber: shelf.shelfNumber,
          shelfPurity: nextShelfPurity,
          planogram: nextPlanogram,
          productCounts: requestProductCounts.map((p) => ({
            runId: p.runId,
            storeId: p.storeId,
            fileName: p.fileName,
            product: p.product,
            aiProductCount: p.aiProductCount ?? null,
            manualProductCount: p.manualProductCount ?? null,
            action: p.action ?? null,
          })),
        },
      ],
      auditDate: storeDetail?.auditedDate,
      userId: Number(localStorage.getItem("userId") || sessionStorage.getItem("userId")),
    };

    await apiClient.put(API_CONFIG.ENDPOINTS.MANUALSHELFCOUNTS.UPDATE_SHELF_REPORT, payload);

    // Once persisted, drop the transient action tag so a later edit of the same
    // row doesn't re-send "ADD"/"DELETE"; deleted rows are dropped from local state.
    const nextProductCounts = requestProductCounts
      .filter((p) => p.action !== "DELETE")
      .map((p) => (p.action ? { ...p, action: null } : p));

    setStoreRecord((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        shelves: prev.shelves.map((s) =>
          s.shelfNumber !== shelfNumber
            ? s
            : { ...s, productCounts: nextProductCounts, shelfPurity: nextShelfPurity, planogram: nextPlanogram }
        ),
      };
    });
  };

  const handleAddProductToShelf = async (shelfNumber, product) => {
    const shelf = storeRecord?.shelves?.find((s) => s.shelfNumber === shelfNumber);
    if (!shelf || !product) return;

    const alreadyOnShelf = shelf.productCounts?.some(
      (p) => p.product?.productId === product.productId
    );
    if (alreadyOnShelf) {
      toast({
        title: "Product is already on this shelf",
        status: "warning",
        duration: 2500,
        isClosable: true,
        position: "top",
      });
      return;
    }

    // Reuse the run/store/file context from a sibling row so the backend can
    // group the manually added product with the rest of this shelf's counts.
    const contextItem =
      shelf.productCounts?.[0] ??
      storeRecord?.shelves?.flatMap((s) => s.productCounts ?? []).find((p) => p.runId != null);

    const newItem = {
      runId: contextItem?.runId ?? null,
      storeId: contextItem?.storeId ?? storeDetail?.storeId ?? null,
      fileName: contextItem?.fileName ?? "",
      product,
      aiProductCount: null,
      manualProductCount: null,
      action: "ADD",
    };

    setAddingProductShelf(shelfNumber);
    try {
      await handleUpdateShelf(shelfNumber, {
        productCounts: [...shelf.productCounts, newItem],
      });
      toast({
        title: "Product added to shelf",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top",
      });
    } catch (err) {
      console.error("addProductToShelf failed:", err?.response?.status, err?.response?.data || err?.message);
      toast({
        title: "Failed to add product. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setAddingProductShelf(null);
    }
  };

  const handleUpdateProductCount = async (shelfNumber, item, values) => {
    const shelf = storeRecord?.shelves?.find((s) => s.shelfNumber === shelfNumber);
    if (!shelf) return;

    const updatedProductCounts = shelf.productCounts.map((p) =>
      p === item ? { ...p, ...values } : p
    );

    const rowKey = `${item.runId ?? ""}-${item.product?.productId ?? ""}-${item.fileName ?? ""}`;
    setSavingRowKey(rowKey);
    try {
      await handleUpdateShelf(shelfNumber, { productCounts: updatedProductCounts });
      toast({
        title: "Shelf counts updated successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top",
      });
    } catch (err) {
      console.error("updateShelfReport failed:", err?.response?.status, err?.response?.data || err?.message);
      toast({
        title: "Failed to update shelf counts. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      throw err;
    } finally {
      setSavingRowKey(null);
    }
  };

  const handleDeleteProductFromShelf = async (shelfNumber, item) => {
    const shelf = storeRecord?.shelves?.find((s) => s.shelfNumber === shelfNumber);
    if (!shelf) return;

    if (!window.confirm(`Remove ${item.product?.productName ?? "this product"} from the shelf?`)) {
      return;
    }

    const rowKey = `${item.runId ?? ""}-${item.product?.productId ?? ""}-${item.fileName ?? ""}`;
    setDeletingRowKey(rowKey);
    try {
      const productCountsForRequest = shelf.productCounts.map((p) =>
        p === item ? { ...p, action: "DELETE" } : p
      );
      await handleUpdateShelf(shelfNumber, { productCounts: productCountsForRequest });
      toast({
        title: "Product removed from shelf",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top",
      });
    } catch (err) {
      console.error("deleteProductFromShelf failed:", err?.response?.status, err?.response?.data || err?.message);
      toast({
        title: "Failed to remove product. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setDeletingRowKey(null);
    }
  };

  const handleUpdateShelfPurity = async (shelfNumber, newPurity) => {
    setSavingPurity(true);
    try {
      await handleUpdateShelf(shelfNumber, { shelfPurity: newPurity });
      toast({
        title: "Shelf purity updated successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top",
      });
    } catch (err) {
      console.error("updateShelfReport (purity) failed:", err?.response?.status, err?.response?.data || err?.message);
      toast({
        title: "Failed to update shelf purity. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setSavingPurity(false);
    }
  };

  const handleUpdatePlanogram = async (shelfNumber, newPlanogram) => {
    setSavingPlanogram(true);
    try {
      await handleUpdateShelf(shelfNumber, { planogram: newPlanogram });
      toast({
        title: "Planogram updated successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top",
      });
    } catch (err) {
      console.error("updateShelfReport (planogram) failed:", err?.response?.status, err?.response?.data || err?.message);
      toast({
        title: "Failed to update planogram. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setSavingPlanogram(false);
    }
  };

  return (
    <Box
      minH="100vh" bg="gray.50"
      py={{ base: 6, md: 10 }}
      px={{ base: 3, sm: 4, md: 8 }}
      fontFamily="'Segoe UI', sans-serif"
    >
      {/* ── Page header ── */}
      <Flex
        justify="space-between"
        align={{ base: "flex-start", md: "flex-end" }}
        direction={{ base: "column", md: "row" }}
        gap={4} mb={6} mt={6}
      >
        <Box>
          <Heading size="lg" color="gray.800" mb={1}>
            Manual vs AI Count Comparison
          </Heading>
          <Text color="gray.500" fontSize="sm">
            Verify AI-detected SKUS counts against manual audit data for retail visicoolers
          </Text>
        </Box>
        <DateRangeFilter
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={handleFromDateChange}
          onToDateChange={handleToDateChange}
        />
      </Flex>

      {/* ── Store selector (search bar + dropdown, unchanged) ── */}
      <Flex gap={6} wrap="wrap" mb={6} align="flex-start">
        <Box maxW={{ base: "100%", lg: "420px" }} flex="1" minW={{ base: "100%", sm: "280px" }}>
          <Text
            fontSize="10px" fontWeight="900" color="gray.900"
            textTransform="uppercase" letterSpacing="widest" mb={1}
          >
            Select Outlet
          </Text>
          <Select
            options={storeOptions}
            value={selectedStore}
            onChange={handleStoreChange}
            placeholder={
              !fromDate || !toDate
                ? "Select a date range first..."
                : loadingStores
                ? "Loading stores..."
                : storeOptions.length === 0
                ? "No stores found for this period"
                : "Search or select a store..."
            }
            isClearable
            isLoading={loadingStores}
            isDisabled={!fromDate || !toDate || loadingStores}
            styles={storeSelectStyles}
            noOptionsMessage={() => "No stores found for the selected date range"}
          />
          {storeError && (
            <Text mt={2} fontSize="xs" color="red.500" fontWeight="600">
              {storeError}
            </Text>
          )}
        </Box>

        <Box maxW={{ base: "100%", lg: "420px" }} flex="1" minW={{ base: "100%", sm: "280px" }}>
          <Text
            fontSize="10px" fontWeight="900" color="gray.900"
            textTransform="uppercase" letterSpacing="widest" mb={1}
          >
            Select Auditor
          </Text>
          <Select
            options={auditorOptions}
            value={selectedAuditor}
            onChange={handleAuditorChange}
            placeholder={
              !fromDate || !toDate
                ? "Select a date range first..."
                : loadingAuditors
                ? "Loading auditors..."
                : auditorOptions.length === 0
                ? "No auditors found"
                : "Select Auditor"
            }
            isClearable
            isLoading={loadingAuditors}
            isDisabled={!fromDate || !toDate || loadingAuditors}
            styles={storeSelectStyles}
            noOptionsMessage={() => "No auditors found"}
          />
          {auditorError && (
            <Text mt={2} fontSize="xs" color="red.500" fontWeight="600">
              {auditorError}
            </Text>
          )}
        </Box>

        <Box>
          <Text
            fontSize="10px" fontWeight="900" color="gray.900"
            textTransform="uppercase" letterSpacing="widest" mb={2}
          >
            Store Status
          </Text>
          <RadioGroup value={checkedFilter} onChange={handleCheckedFilterChange}>
            <HStack spacing={5}>
              <Radio value="not-checked" colorScheme="red">
                <HStack spacing={1.5}>
                  <Text fontSize="sm" fontWeight="600" color="gray.700">Not Marked</Text>
                  <Badge
                    px={2} borderRadius="full" fontSize="15px" fontWeight="700"
                    bg="red.100" color="gray.700"
                  >
                    {loadingStatusCounts ? "…" : statusCounts.notMarked}
                  </Badge>
                </HStack>
              </Radio>
              <Radio value="checked" colorScheme="red">
                <HStack spacing={1.5}>
                  <Text fontSize="sm" fontWeight="600" color="gray.700">Marked</Text>
                  <Badge
                    px={2} borderRadius="full" fontSize="15px" fontWeight="700"
                    bg="red.100" color="gray.700"
                  >
                    {loadingStatusCounts ? "…" : statusCounts.marked}
                  </Badge>
                </HStack>
              </Radio>
            </HStack>
          </RadioGroup>
        </Box>
      </Flex>

      {reportError && (
        <Text mb={4} fontSize="sm" color="red.500" fontWeight="600">
          {reportError}
        </Text>
      )}

      {/* ── Empty state ── */}
      {!loadingReport && !storeRecord ? (
        <Center
          h="280px" bg="white" borderRadius="2xl"
          border="2px dashed" borderColor="gray.200"
          flexDir="column" gap={4}
        >
          <Box p={5} borderRadius="full" bg="gray.100">
            <Icon as={FiActivity} boxSize={10} color="gray.300" />
          </Box>
          <VStack spacing={1}>
            <Text color="gray.600" fontWeight="semibold">No store data available</Text>
            <Text color="gray.400" fontSize="sm">
              {!fromDate || !toDate
                ? "Set a date range to load store data"
                : "No stores found for the selected date range"}
            </Text>
          </VStack>
        </Center>
      ) : loadingReport ? (
        <Center h="280px" bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.200">
          <Spinner size="lg" color="red.500" thickness="4px" />
        </Center>
      ) : (
        <>
          {/* ── Store info panel ── */}
          <StoreInfoPanel storeDetail={storeDetail} />

          {/* ── Main split panel ── */}
          <Flex gap={6} flexDir={{ base: "column", xl: "row" }} align="flex-start">

            {/* Left: Cooler image viewer */}
            <Box
              bg="white" borderRadius="2xl" boxShadow="lg"
              border="1px solid" borderColor="gray.200"
              p={5} w={{ base: "100%", xl: "480px" }} flexShrink={0}
            >
              <HStack justify="space-between" mb={4}>
                <VStack align="flex-start" spacing={0}>
                  <Text fontWeight="700" fontSize="sm" color="gray.700">Visicooler Image</Text>
                  <Text fontSize="xs" color="gray.400">Annotated cooler view</Text>
                </VStack>
                <Badge
                  px={3} py={1} borderRadius="full" fontSize="10px" fontWeight="700"
                  bg="red.50" color={BRAND} border="1px solid" borderColor="red.100"
                  whiteSpace="normal" wordBreak="break-word" textAlign="right"
                >
                  {storeDetail?.storeName}
                </Badge>
              </HStack>

              <CoolerImagePanel
                images={coolerImages}
                imageIndex={imageIndex}
                setImageIndex={setImageIndex}
                imageRotation={imageRotation}
                setImageRotation={setImageRotation}
                storeName={storeDetail?.storeName}
              />
            </Box>

            {/* Right: Shelf-wise summary table */}
            <Box
              bg="white" borderRadius="2xl" boxShadow="sm"
              border="1px solid" borderColor="gray.200"
              p={5} flex="1" minW={0} display="flex" flexDir="column"
            >
              <HStack justify="space-between" align="flex-start" mb={4} wrap="wrap" rowGap={2}>
                <Box flexShrink={0}>
                  <Text fontWeight="700" fontSize="sm" color="gray.700" whiteSpace="nowrap">
                    Shelf-wise SKUS Count Comparison
                  </Text>
                </Box>
                <HStack spacing={4} wrap="wrap" rowGap={2}>
                  <HStack spacing={2}>
                    <Badge bg="red.100" borderRadius="full"  px={3} py={1} border="1 px solid" >
                      Shelf Purity
                      </Badge>
                
                    <ChakraSelect
                      size="sm"
                      width="80px"
                      borderRadius="lg"
                      value={currentShelf?.shelfPurity ?? "N"}
                      isDisabled={!currentShelf || savingPurity}
                      onChange={(e) => handleUpdateShelfPurity(currentShelf.shelfNumber, e.target.value)}
                    >
                      <option value="Y">Y</option>
                      <option value="N">N</option>
                    </ChakraSelect>
                    {savingPurity && <Spinner size="xs" color="red.500" />}
                  </HStack>
                  <HStack spacing={2}>
                    <Badge bg="red.100" borderRadius="full"  px={3} py={1} border="1 px solid" >
                      Planogram
                      </Badge>

                    <ChakraSelect
                      size="sm"
                      width="80px"
                      borderRadius="lg"
                      value={currentShelf?.planogram ?? "N"}
                      isDisabled={!currentShelf || savingPlanogram}
                      onChange={(e) => handleUpdatePlanogram(currentShelf.shelfNumber, e.target.value)}
                    >
                      <option value="Y">Y</option>
                      <option value="N">N</option>
                    </ChakraSelect>
                    {savingPlanogram && <Spinner size="xs" color="red.500" />}
                  </HStack>
                  <Button
                    size="sm"
                    colorScheme={isValidated ? "green" : "red"}
                    variant="solid"
                    leftIcon={isValidated ? <FiCheckCircle /> : undefined}
                    isLoading={validating}
                    isDisabled={!storeRecord || isValidated}
                    onClick={handleValidateStore}
                  >
                    {isValidated ? "Checked" : "Mark as Checked"}
                  </Button>
                </HStack>
              </HStack>

              <ShelfSummaryTable
                shelves={storeRecord?.shelves}
                shelfIndex={shelfIndex}
                onPrevShelf={handlePrevShelf}
                onNextShelf={handleNextShelf}
                auditorName={storeDetail?.auditorName}
                auditedDate={storeDetail?.auditedDate}
                onUpdateCount={handleUpdateProductCount}
                savingRowKey={savingRowKey}
                availabilityProducts={availabilityProducts}
                loadingProducts={loadingProducts}
                onAddProduct={handleAddProductToShelf}
                addingProductShelf={addingProductShelf}
                onDeleteProduct={handleDeleteProductFromShelf}
                deletingRowKey={deletingRowKey}
              />
            </Box>

          </Flex>

          {/* ── Store pagination bar ── */}
          <HStack justify="space-between" flexWrap="wrap" gap={2}>
            <Button
              size="md" leftIcon={<FiChevronLeft />} variant="outline"
              isDisabled={page === 0 || loadingReport}
              onClick={handlePrevStore}
            >
              Previous Store
            </Button>
            <Text fontSize="sm" fontWeight="600" color="gray.600">
              Store {pageInfo ? page + 1 : 0} of {pageInfo?.totalElements ?? 0}
            </Text>
            <Button
              size="md" rightIcon={<FiChevronRight />} variant="outline"
              isDisabled={!pageInfo || pageInfo.last || loadingReport}
              onClick={handleNextStore}
            >
              Next Store
            </Button>
          </HStack>
        </>
      )}
    </Box>
  );
}